import {
  Button,
  ButtonVariants,
  HStack,
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MobileHeaderSpacer,
  Stack,
  Text,
  useMixpanel,
  VStack,
} from '@talos/kyoko';
import { startTransition, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisableMobileLayoutForSession } from '../../hooks/useMobileLayoutState';
import { MobileAppHeader } from './MobileAppHeader';

interface MobileUnsupportedSectionPlaceholderProps {
  label: string;
}

export function MobileUnsupportedSectionPlaceholder({ label }: MobileUnsupportedSectionPlaceholderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const mixpanel = useMixpanel();
  const [, setIsMobileLayoutDisabledForSession] = useDisableMobileLayoutForSession();

  useEffect(() => {
    mixpanel.track(MixpanelEvent.ViewMobileUnsupportedSection, {
      [MixpanelEventProperty.Path]: location.pathname,
      [MixpanelEventProperty.Section]: label,
    });
  }, [label, location.pathname, mixpanel]);

  const handleBack = useCallback(() => {
    mixpanel.track(MixpanelEvent.BackToMobilePage, {
      [MixpanelEventProperty.Path]: location.pathname,
      [MixpanelEventProperty.Section]: label,
    });
    navigate(-1);
  }, [label, location.pathname, mixpanel, navigate]);

  const handleContinueToDesktop = useCallback(() => {
    mixpanel.track(MixpanelEvent.ContinueToDesktopVersion, {
      [MixpanelEventProperty.Path]: location.pathname,
      [MixpanelEventProperty.Section]: label,
    });
    startTransition(() => setIsMobileLayoutDisabledForSession(true));
  }, [label, location.pathname, mixpanel, setIsMobileLayoutDisabledForSession]);

  return (
    <>
      <MobileAppHeader />
      <MobileHeaderSpacer />
      <VStack
        data-testid="mobile-unsupported-section-placeholder"
        background="backgroundContent"
        flex="1"
        gap="spacing20"
        p="spacing24"
        textAlign="center"
      >
        <Stack alignItems="center" gap="spacing16">
          <Icon color="colorTextSubtle" icon={IconName.DeviceDesktop} size={48} />
          <Text as="h1" color="colorTextImportant" fontSize="fontSizeXl" fontWeight={500} m={0}>
            {label} is not available on mobile
          </Text>
          <Text color="colorTextSubtle" fontSize="fontSizeMd" maxWidth="320px">
            Continue to the desktop version for full access.
          </Text>
          <HStack gap="spacing8">
            <Button startIcon={IconName.ArrowLeft} data-testid="mobile-back-to-mobile-button" onClick={handleBack}>
              Back
            </Button>
            <Button
              data-testid="mobile-continue-to-desktop-button"
              onClick={handleContinueToDesktop}
              variant={ButtonVariants.Primary}
              endIcon={IconName.ArrowRight}
            >
              Desktop version
            </Button>
          </HStack>
        </Stack>
      </VStack>
    </>
  );
}
